import React, { useEffect, useRef, useState } from 'react';

import './GaussianHat.css';

const padding = { left: 48, right: 20, top: 24, bottom: 48 };
const W = 900;
const H = 420;
const x0Px = padding.left;
const y0Px = H - padding.bottom;

function createOrdersBasedModel(pStar, peakOrders, k = 8) {
  const pStarSafe = pStar > 0 ? pStar : 1;
  const peakOrdersSafe = peakOrders > 0 ? peakOrders : 1;

  const expectedOrders = (p) => {
    return peakOrdersSafe * Math.exp((1 - (p / pStarSafe) ** k) / k);
  };

  const expectedEarnings = (p) => p * expectedOrders(p);

  return {
    pStar,
    peakOrders,
    k,
    q: expectedOrders,
    R: expectedEarnings,
  };
}

function sampleR(model, pMin, pMax, steps) {
  const pts = [];
  const step = (pMax - pMin) / steps;
  for (let i = 0; i <= steps; i++) {
    const p = pMin + step * i;
    pts.push({ x: p, y: model.R(p) });
  }
  return pts;
}

function pathFromPoints(points, xToPx, yToPx) {
  if (!points.length) return '';
  let d = `M${xToPx(points[0].x)} ${yToPx(points[0].y)}`;
  for (let i = 1; i < points.length; i++) {
    d += ` L${xToPx(points[i].x)} ${yToPx(points[i].y)}`;
  }
  return d;
}

function areaPathUpTo(points, price, xToPx, yToPx, x0Px, y0Px) {
  const left = points.filter((pt) => pt.x <= price);
  if (left.length === 0) return '';
  let d = `M${x0Px} ${y0Px}`;
  d += ` L${xToPx(left[0].x)} ${yToPx(left[0].y)}`;
  for (let i = 1; i < left.length; i++) {
    d += ` L${xToPx(left[i].x)} ${yToPx(left[i].y)}`;
  }
  d += ` L${xToPx(left[left.length - 1].x)} ${y0Px} Z`;
  return d;
}

function GaussianHat({
  recommendedPrice = 1,
  guestPostPrice,
  guestPostLinkLimit,
  topValue,
  setOrdersNumber,
  setRequestedPrice,
  peakOrders,
  setOverprice,
  onResetToInitial,
  isGuestPost = true,
}: {
  recommendedPrice: number;
  guestPostPrice?: number;
  guestPostLinkLimit: number;
  topValue: number;
  setOrdersNumber: (value: number) => void;
  setRequestedPrice: (value: number) => void;
  peakOrders: number;
  setOverprice: (value: boolean) => void;
  onResetToInitial?: () => void;
  isGuestPost?: boolean;
}): JSX.Element {
  const ref = useRef(null);
  const [priceValue, setPrice] = useState(recommendedPrice);
  const badgePriceRef = useRef(null);

  const pMinI = 0;
  const pMaxI = 2 * recommendedPrice;

  const setBadgePrice = (value) => {
    (badgePriceRef.current as any).innerText = Math.round(value);
  };

  // Initialize model price once when recommendedPrice changes (do not reset on link limit changes)
  useEffect(() => {
    if (recommendedPrice) {
      const clamped = Math.max(pMinI, Math.min(pMaxI, recommendedPrice));
      setPrice(clamped);
      if (badgePriceRef.current) {
        setBadgePrice(clamped);
      }
    }
  }, []);

  useEffect(() => {
    const clamped = Math.max(pMinI, Math.min(pMaxI, guestPostPrice));
    setPrice(clamped);
    setBadgePrice(guestPostPrice);
  }, [guestPostPrice, pMinI, pMaxI]);

  // When number of links changes, keep the slider position but update the badge dollar value
  useEffect(() => {
    if (badgePriceRef.current) {
      setBadgePrice(priceValue);
    }
  }, [guestPostLinkLimit, priceValue]);

  useEffect(() => {
    const yMaxLbl = ref.current.querySelector('#yMaxLbl');
    if (yMaxLbl) yMaxLbl.textContent = Math.round(topValue).toString();
  }, [topValue]);

  // Static model/curve rendering effect: runs only when model inputs change (pStarI, peakOrders)
  useEffect(() => {
    if (!ref.current) return;

    const curveEl = ref.current.querySelector('#curve');
    const topDotEl = ref.current.querySelector('#topDot') as SVGCircleElement | null;
    const topVlineEl = ref.current.querySelector('#topVline') as SVGLineElement | null;

    const pMin = Number(pMinI);
    const pMax = Number(pMaxI);
    const model = createOrdersBasedModel(recommendedPrice, peakOrders);
    const pts = sampleR(model, pMin, pMax, 320);

    const yMax = Math.max(topValue, 1);

    const plotW = W - padding.left - padding.right;
    const plotH = H - padding.top - padding.bottom;
    const xToPx = (x) => x0Px + ((x - pMin) / (pMax - pMin)) * plotW;
    const yToPx = (y) => y0Px - (y / yMax) * plotH;

    // Draw static curve
    curveEl.setAttribute('d', pathFromPoints(pts, xToPx, yToPx));

    // Place TOP point + vertical once (optimal)
    const pTop = Number(recommendedPrice);
    const rTop = model.R(pTop);
    const topPx = xToPx(pTop);
    const topPy = yToPx(rTop);
    if (topDotEl) {
      topDotEl.setAttribute('cx', String(topPx));
      topDotEl.setAttribute('cy', String(topPy));
    }
    if (topVlineEl) {
      topVlineEl.setAttribute('x1', String(topPx));
      topVlineEl.setAttribute('x2', String(topPx));
    }

    // After static draw, also update moving parts for current price
    const updateForPrice = () => {
      const areaEl = ref.current.querySelector('#area');
      const dotEl = ref.current.querySelector('#dot');
      const vlineEl = ref.current.querySelector('#vline');
      const priceRange = ref.current.querySelector('#price') as HTMLInputElement | null;
      if (!priceRange) return;
      const price = Number(priceRange.value);
      const percent = ((price - pMin) / (pMax - pMin)) * 100;
      priceRange.style.background = `linear-gradient(to right, #221CB6 0%, #221CB6 ${percent}%, transparent ${percent}%, transparent 100%)`;
      priceRange.style.borderRadius = '10px';

      areaEl.setAttribute('d', areaPathUpTo(pts, price, xToPx, yToPx, x0Px, y0Px));

      const rNow = model.R(price);
      const px = xToPx(price);
      const py = yToPx(rNow);
      dotEl.setAttribute('cx', String(px));
      dotEl.setAttribute('cy', String(py));
      vlineEl.setAttribute('x1', String(px));
      vlineEl.setAttribute('x2', String(px));

      const qNow = Number(model.q(Math.round(price)).toFixed(2));
      setOrdersNumber(qNow);
    };

    updateForPrice();

    const onTopClick = () => {
      const newPrice = Math.round(Number(recommendedPrice));
      setRequestedPrice(newPrice);
      const priceInput = ref.current.querySelector('#price') as HTMLInputElement | null;
      if (priceInput) priceInput.value = String(newPrice);
      if (badgePriceRef.current) {
        setBadgePrice(newPrice);
      }
      setPrice(newPrice);
      setOverprice(false);
    };

    if (topDotEl) {
      topDotEl.setAttribute('tabindex', '0');
      topDotEl.addEventListener('click', onTopClick);
      topDotEl.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') onTopClick();
      });
    }

    return () => {
      if (topDotEl) {
        topDotEl.removeEventListener('click', onTopClick);
      }
    };
  }, [peakOrders, guestPostLinkLimit, recommendedPrice, pMinI, pMaxI]);

  // Moving elements update: runs when price changes
  useEffect(() => {
    if (!ref.current) return;
    const areaEl = ref.current.querySelector('#area');
    const dotEl = ref.current.querySelector('#dot');
    const vlineEl = ref.current.querySelector('#vline');
    const priceRange = ref.current.querySelector('#price') as HTMLInputElement | null;
    if (!priceRange) return;

    const pMin = Number(pMinI);
    const pMax = Number(pMaxI);
    const model = createOrdersBasedModel(recommendedPrice, peakOrders);

    const price = Number(priceRange.value);
    const percent = ((price - pMin) / (pMax - pMin)) * 100;
    priceRange.style.background = `linear-gradient(to right, #221CB6 0%, #221CB6 ${percent}%, transparent ${percent}%, transparent 100%)`;
    priceRange.style.borderRadius = '10px';

    const plotW = W - padding.left - padding.right;
    const plotH = H - padding.top - padding.bottom;
    const xToPx = (x) => x0Px + ((x - pMin) / (pMax - pMin)) * plotW;

    // y scale uses the same yMax as model's own max; recompute quickly without touching curve
    const yMax = Math.max(topValue, 1);
    const yToPx = (y) => y0Px - (y / yMax) * plotH;

    // Regenerate area path up to current price without changing curve
    // We need pts for area; resample quickly (cheap) without redrawing curve
    const pts = sampleR(model, pMin, pMax, 320);
    areaEl.setAttribute('d', areaPathUpTo(pts, price, xToPx, yToPx, x0Px, y0Px));

    const rNow = model.R(price);
    const px = xToPx(price);
    const py = yToPx(rNow);
    dotEl.setAttribute('cx', String(px));
    dotEl.setAttribute('cy', String(py));
    vlineEl.setAttribute('x1', String(px));
    vlineEl.setAttribute('x2', String(px));

    const qNow = Number(model.q(Math.round(priceValue)).toFixed(2));
    setOrdersNumber(qNow);
  }, [priceValue, peakOrders, recommendedPrice, pMinI, pMaxI, guestPostPrice]);

  const handlePriceInBadgeKeydown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const handlePriceInBadgeChange = (event) => {
    let badgePrice = Number(event.target.innerText);

    if (isNaN(badgePrice)) {
      return;
    }

    const typedPrice = Math.round(badgePrice);

    if (badgePrice > pMaxI) {
      setOverprice(true);
    } else {
      setOverprice(false);
    }

    if (badgePrice < 0) {
      badgePrice = 0;
      event.target.innerText = Math.round(badgePrice).toString();
    } else if (badgePrice > pMaxI) {
      // allow higher price in input
    } else if (badgePrice.toString() !== event.target.innerText) {
      event.target.innerText = Math.round(badgePrice).toString();
    }

    const visualPrice = Math.max(1, Math.min(typedPrice, pMaxI));
    const actualPrice = Math.max(1, typedPrice);

    setPrice(visualPrice);
    setRequestedPrice(actualPrice);
  };

  const range = pMaxI - pMinI;
  const percent = (priceValue - pMinI) / range;

  const trackWidth = 428;
  const handleWidth = 60;

  const px = percent * (trackWidth - handleWidth) + handleWidth / 2;

  return (
    <div className="wrap-gaussian" ref={ref}>
      <div className="card">
        <div className="row">
          <div className="slider-wrapper">
            <input
              id="price"
              type="range"
              min={pMinI}
              max={pMaxI}
              step="1"
              onChange={(e) => {
                const newPrice = e.target.valueAsNumber;
                setRequestedPrice(newPrice);
                setBadgePrice(newPrice);
                setPrice(newPrice);
                setOverprice(false);
              }}
              value={priceValue}
            />
            <span
              className="badge"
              style={{
                position: 'absolute',
                top: '30px',
                left: `${px}px`,
                transform: 'translateX(-50%)',
              }}
            >
              <span>
                $
                <span
                  className="badge-price"
                  contentEditable
                  onKeyDown={handlePriceInBadgeKeydown}
                  onInput={handlePriceInBadgeChange}
                  ref={badgePriceRef}
                />
              </span>
              {/* $ */}
              {/* <input */}
              {/*  type="text" */}
              {/*  value={`${Math.floor(guestPostLinkLimit * recommendedPrice)}`} */}
              {/*  onChange={(e) => { */}
              {/*    console.log('=== e.target ===', e.target.valueAsNumber); */}
              {/*    console.log('=== e.target 22 ===', e.target.value); */}
              {/*    console.log('=== e.target 22 ===', parseInt(e.target.value, 10) / guestPostLinkLimit); */}
              {/*    setRequestedPrice(parseInt(e.target.value, 10) / guestPostLinkLimit); */}
              {/*    setPrice(parseInt(e.target.value, 10)); */}
              {/*  }} */}
              {/* /> */}
            </span>
          </div>
        </div>

        <div className="chart">
          <svg
            id="svg"
            viewBox="0 -35 900 450"
            preserveAspectRatio="xMidYMid meet"
            aria-label="Revenue chart"
          >
            <defs>
              <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
                <path d="M 60 0 L 0 0 0 60" fill="none" stroke="var(--grid)" strokeWidth="1" />
              </pattern>
            </defs>
            <rect x="0" y="0" width="900" height="440" fill="url(#grid)" />

            <g id="axes" stroke="var(--line)">
              <line x1="48" y1="372" x2="880" y2="372" />
              <line x1="48" y1="372" x2="48" y2="44" />
            </g>

            <g>
              <text x="10" y="-15" style={{ fontSize: 24 }} fill="#C7C6D7">
                Total earnings (estimate)
              </text>
              <text x="655" y="413" style={{ fontSize: 24 }} fill="#C7C6D7">
                {isGuestPost ? 'Guest post price' : 'Link insertion price'}
              </text>
              <text id="yMaxEl" x="1" y="41" style={{ fontSize: 32 }} fill="var(--muted)">
                $
                <tspan id="yMaxLbl" style={{ fontSize: 32 }} fill="var(--muted)">
                  400
                </tspan>
              </text>
            </g>

            <path id="area" fill="var(--accent-soft)" />
            <path id="curve" fill="none" stroke="var(--line)" strokeWidth="2" />
            <line
              id="topVline"
              x1="0"
              y1="372"
              x2="0"
              y2="60"
              stroke="var(--accent)"
              strokeDasharray="2 6"
              opacity="0.5"
            />
            <circle
              id="topDot"
              r="7"
              fill="white"
              stroke="var(--accent)"
              strokeWidth="3"
              style={{ cursor: 'pointer' }}
            >
              <title>Jump to optimal price</title>
            </circle>
            <line
              id="vline"
              x1="0"
              y1="372"
              x2="0"
              y2="60"
              stroke="var(--accent)"
              strokeDasharray="4 4"
            />
            <circle id="dot" r="5" fill="var(--accent)" stroke="white" strokeWidth="2" />
          </svg>
        </div>
      </div>
    </div>
  );
}

export default GaussianHat;
